// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a Situation room wall display (`/?wall=1`, slice CR1,
 * docs/AYNI_XR_CONTROL_ROOM_PLAN.md). A browser on the room's screen with no
 * account (decision CR-D2: it takes no seat):
 *
 * 1. **Pairing.** It shows a short code; a member types it in the Situation
 *    room panel and names the screen. The wall then holds its own token,
 *    in this browser only.
 * 2. **The board.** It plays the room's board in a loop, each step on its
 *    stream's latest frame, with the step's title, a clock in UTC and local
 *    time, and on every globe how old its data is (CR2). Nothing on it can
 *    be pressed. After each loop it asks for the board again (a member may
 *    have changed it).
 * 3. **A briefing.** When a member puts a meeting on the walls, the wall
 *    joins it as one more person in its audience, and comes back to the
 *    board when the meeting is taken off or ends.
 *
 * It never stops by itself: a failed request keeps the last board playing
 * with an "offline" mark, the screen is kept awake, and every three hours
 * (at the end of a loop, when the network answers) the page reloads to pick
 * up new releases and a new version of the app.
 */

import { getLocale, t } from '../i18n'
import { localized, type Khipu } from '../services/khipu'
import { planStep } from '../services/khipuPlayback'
import { resolveDatasetKey } from '../services/lessonService'
import type { OwnLayersController } from '../services/ownLayers'
import { loadWall, pollPairing, startPairing, storeWallPairing, storedWallPairing, type WallPairing } from '../services/situationClient'
import { formatPairCode, type WallState } from '../services/situationRoom'
import { defaultBoard } from '../services/situationTemplates'
import { applyStepToGlobe, type KhipuPlayerHost } from './khipuPlayerUI'
import { ageText, clockText, type FreshnessRow } from './situationPanel'

export interface WallHost {
  /** What the Khipu player may do with this page's globes. */
  khipuHost(): KhipuPlayerHost
  freshness(): FreshnessRow[]
  /** Each globe's box on the screen, globe 1 first. */
  globeRects(): Array<DOMRect | null>
  ownLayers: OwnLayersController
  /** Lend the licence's packages and globes to this page (no account). */
  setGrant(grant: { packages: readonly string[]; globes: number } | null): void
  /** Run a step's changes past the page's lock (nothing on a wall can be pressed, but the wall itself changes the globes). */
  acting(change: () => Promise<void>): Promise<void>
}

/** A step without its own timer stays this long on the wall. */
const DEFAULT_STEP_S = 30
const POLL_PAIR_MS = 3_000
const BRIEFING_POLL_MS = 30_000
const CHROME_MS = 15_000
const RELOAD_AFTER_MS = 3 * 60 * 60 * 1000

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms))

/** Whether this page is a wall display. */
export function isWallDisplay(search: string = location.search): boolean {
  return new URLSearchParams(search).get('wall') === '1'
}

let wakeLock: { release(): Promise<void> } | null = null

async function keepAwake(): Promise<void> {
  const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void>; addEventListener(t: string, f: () => void): void }> } }
  if (!nav.wakeLock || document.visibilityState !== 'visible') return
  try {
    const lock = await nav.wakeLock.request('screen')
    wakeLock = lock
    lock.addEventListener('release', () => {
      if (wakeLock === lock) wakeLock = null
    })
  } catch {
    /* refused (battery saver): the screen's own settings decide */
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  node.className = cls
  if (text) node.textContent = text
  return node
}

/** The bar, caption, chips and offline mark; updated in place. */
function mountChrome(host: WallHost, name: () => string) {
  const bar = el('div', 'wall-bar')
  const label = el('span', 'wall-name')
  const clock = el('span', 'wall-clock')
  bar.append(label, clock)
  const caption = el('div', 'wall-caption')
  caption.hidden = true
  const chips = el('div', 'wall-chips')
  chips.setAttribute('aria-hidden', 'true')
  const offline = el('div', 'wall-offline', t('situation.wall.offline'))
  offline.hidden = true
  document.body.append(bar, caption, chips, offline)

  function update(): void {
    label.textContent = name() ? t('situation.wall.named', { name: name() }) : t('situation.title')
    clock.textContent = clockText()
    const rows = host.freshness()
    const rects = host.globeRects()
    // Just inside each globe's top edge, below the wall's bar where it covers that edge.
    const barBottom = bar.getBoundingClientRect().bottom
    chips.replaceChildren(...rows.flatMap((r) => {
      const rect = rects[r.globe - 1]
      if (!rect) return []
      const chip = el('span', `wall-chip${r.level ? ` sr-level-${r.level}` : ''}`)
      chip.textContent = r.ageMs !== null ? `${r.title} · ${ageText(r.ageMs)}` : r.title
      chip.style.insetInlineStart = `${Math.round(rect.left + 12)}px`
      chip.style.insetBlockStart = `${Math.round(Math.max(rect.top + 12, barBottom + 6))}px`
      return [chip]
    }))
  }
  update()
  const timer = window.setInterval(update, CHROME_MS)
  return {
    update,
    setCaption(text: string) {
      caption.textContent = text
      caption.hidden = !text
    },
    setOffline(on: boolean) {
      offline.hidden = !on
    },
    dispose() {
      window.clearInterval(timer)
      bar.remove()
      caption.remove()
      chips.remove()
      offline.remove()
    },
  }
}

/** Show a pairing code until a member claims it; resolves with the wall's pairing. */
async function pairThisWall(): Promise<WallPairing> {
  const screen = el('div', 'wall-pairing')
  const box = el('div', 'wall-pairing-box')
  const title = el('h1', '', t('situation.wall.pairTitle'))
  const code = el('div', 'wall-code', '···-···')
  const how = el('p', '', t('situation.wall.pairHow'))
  const status = el('p', 'wall-pair-status')
  status.setAttribute('role', 'status')
  box.append(title, code, how, status)
  screen.append(box)
  document.body.append(screen)
  try {
    for (;;) {
      const start = await startPairing()
      if (!start.ok) {
        status.textContent = t('situation.wall.pairRetry')
        await sleep(start.error === 'rate_limited' ? 60_000 : 10_000)
        continue
      }
      code.textContent = formatPairCode(start.body.code)
      status.textContent = ''
      while (Date.now() < start.body.expiresAt) {
        await sleep(POLL_PAIR_MS)
        const poll = await pollPairing(start.body.code, start.body.poll)
        if (!poll.ok) {
          if (poll.error === 'not_found') break
          continue
        }
        if (poll.body.state === 'paired') {
          const pairing = { token: poll.body.token, displayId: poll.body.displayId, name: poll.body.name }
          storeWallPairing(pairing)
          return pairing
        }
      }
      // The code ran out: a new one.
    }
  } finally {
    screen.remove()
  }
}

/**
 * Run this page as a wall display. `following`: the page was opened on a
 * briefing meeting (`?room=…&output=room&wall=1`) and only watches for it
 * to come off the walls.
 */
export async function startWallDisplay(host: WallHost): Promise<void> {
  document.body.classList.add('wall-display')
  void keepAwake()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !wakeLock) void keepAwake()
  })
  const params = new URLSearchParams(location.search)
  const followingCode = params.get('room')
  let pairing = storedWallPairing()
  if (!pairing) {
    if (followingCode) return location.replace('/?wall=1')
    pairing = await pairThisWall()
  }
  let state = null as WallState | null
  const chrome = mountChrome(host, () => state?.name ?? pairing?.name ?? '')

  /** Ask for the wall's state; false when it is no longer paired. */
  async function refresh(): Promise<boolean> {
    const res = await loadWall(pairing!.token)
    if (res.ok) {
      state = res.body
      chrome.setOffline(false)
      host.setGrant({ packages: state.packages, globes: state.globes })
      host.ownLayers.setList(state.layers, { kind: 'wall', token: pairing!.token })
      return true
    }
    if (res.error === 'unauthorized' || res.error === 'plan_required') {
      storeWallPairing(null)
      return false
    }
    chrome.setOffline(true)
    return true
  }

  const briefingCode = (): string | null => (state?.briefing && state.briefing.until > Date.now() ? state.briefing.code : null)

  if (!(await refresh())) return location.replace('/?wall=1')

  if (followingCode) {
    // Watching a briefing: back to the board once it is taken off the walls or ends.
    for (;;) {
      await sleep(BRIEFING_POLL_MS)
      if (!(await refresh())) return location.replace('/?wall=1')
      if (briefingCode() !== followingCode && navigator.onLine !== false) return location.replace('/?wall=1')
      chrome.update()
    }
  }

  const startedAt = Date.now()
  const khipuHost = host.khipuHost()
  // Asked every half minute while the board plays; a briefing put on the walls, or an unpairing, ends the step shown.
  let unpaired = false
  let wake: (() => void) | null = null
  const stepWait = (ms: number): Promise<void> => new Promise((resolve) => {
    const timer = setTimeout(() => { wake = null; resolve() }, ms)
    wake = () => { clearTimeout(timer); wake = null; resolve() }
  })
  window.setInterval(() => {
    void refresh().then((paired) => {
      if (!paired) unpaired = true
      if (unpaired || briefingCode()) wake?.()
    })
  }, BRIEFING_POLL_MS)
  for (;;) {
    const board: Khipu = state?.board ?? defaultBoard()
    const lang = getLocale()
    for (const step of board.path.steps) {
      const plan = planStep(board, step, lang)
      await host.acting(async () => {
        if (plan.datasetKey !== undefined) {
          if (plan.datasetKey === null) khipuHost.clearDataset?.()
          else {
            const resolved = await resolveDatasetKey(board, plan.datasetKey, lang)
            if (resolved.kind !== 'none' && resolved.kind !== 'unavailable' && khipuHost.currentDatasetId() !== resolved.datasetId) {
              await khipuHost.loadDataset(resolved.datasetId)
            }
          }
        }
        await applyStepToGlobe(khipuHost, plan, () => true, { lang }, board)
      })
      chrome.setCaption(localized(step.title, lang))
      window.setTimeout(() => chrome.update(), 2_000)
      await stepWait((plan.advanceSeconds ?? DEFAULT_STEP_S) * 1000)
      // A briefing put on the walls takes over at the next step.
      if (briefingCode() || unpaired) break
    }
    if (unpaired || !(await refresh())) return location.replace('/?wall=1')
    const briefing = state?.briefing && state.briefing.until > Date.now() ? state.briefing : null
    if (briefing) {
      const url = new URL(briefing.audience, location.origin)
      url.searchParams.set('output', 'room')
      url.searchParams.set('wall', '1')
      return location.assign(`${url.pathname}${url.search}`)
    }
    if (Date.now() - startedAt > RELOAD_AFTER_MS && navigator.onLine !== false) return location.reload()
  }
}
